import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Banner, Card, ErrorNote } from '@/components/Common';
import { Modal } from '@/components/Modal';

interface TrendRow {
  date: string;
  account: 'REVENUE' | 'PLATFORM_FEE_REVENUE' | 'FX_MARGIN_REVENUE' | 'VENDOR_COMMISSION_EXPENSE';
  amount: number;
}

interface BackfillResult {
  ordersProcessed: number;
  ordersSkipped: number;
  feeTotalUsd: number;
  commissionTotalUsd: number;
  fxMarginTotalUsd: number;
}

interface AccountingSummary {
  from: string;
  to: string;
  hotRevenue: number;
  platformFeeRevenue: number;
  fxMarginRevenue: number;
  vendorCommissionExpense: number;
  netRevenue: number;
  dailyTrend: TrendRow[];
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/**
 * Platform profitability — what WE earn, not what a merchant owes. Revenue is HOT's own
 * per-product discount plus our flat platform fee on vendor/company recharges; the only expense
 * is vendor commission. Deliberately separate from SettlementPage, which reconciles a merchant's
 * collected/dispensed/variance rather than answering "are we making money."
 */
export function AccountingPage() {
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [confirmingBackfill, setConfirmingBackfill] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounting-summary', from, to],
    queryFn: () => api.get<AccountingSummary>(
      `/api/admin/accounting/summary?from=${from}&to=${to}`),
  });

  const byDate = new Map<string, { hot: number; fee: number; fx: number; commission: number }>();
  for (const row of data?.dailyTrend ?? []) {
    const entry = byDate.get(row.date) ?? { hot: 0, fee: 0, fx: 0, commission: 0 };
    if (row.account === 'REVENUE') entry.hot = row.amount;
    if (row.account === 'PLATFORM_FEE_REVENUE') entry.fee = row.amount;
    if (row.account === 'FX_MARGIN_REVENUE') entry.fx = row.amount;
    if (row.account === 'VENDOR_COMMISSION_EXPENSE') entry.commission = row.amount;
    byDate.set(row.date, entry);
  }
  const days = [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]));

  return (
    <div className="space-y-4">
      <Card title="Accounting"
            actions={
              <button type="button" className="btn-secondary"
                      onClick={() => setConfirmingBackfill(true)}>
                Backfill historical orders
              </button>
            }>
        <div className="flex flex-wrap items-end gap-3 p-4">
          <label>
            <span className="label">From</span>
            <input type="date" className="input mt-1" value={from}
                   onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            <span className="label">To</span>
            <input type="date" className="input mt-1" value={to}
                   onChange={(e) => setTo(e.target.value)} />
          </label>
          <span className="text-xs text-ink-500">
            Only affects totals — doesn't change the range above.
          </span>
        </div>

        {isLoading && <p className="p-4 text-sm text-ink-500">Loading…</p>}
        {error && <div className="p-4"><ErrorNote error={error} /></div>}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-3 border-t border-ink-200 p-4 sm:grid-cols-2 lg:grid-cols-5">
              <StatTile label="HOT discount revenue" value={data.hotRevenue}
                        hint="USD airtime only — HOT pays zero on ZiG" />
              <StatTile label="Platform fee revenue" value={data.platformFeeRevenue}
                        hint="Flat fee on vendor & company recharges" />
              <StatTile label="FX margin revenue" value={data.fxMarginRevenue}
                        hint="Every ZWG recharge — ZiG's real earning" />
              <StatTile label="Vendor commission expense" value={data.vendorCommissionExpense}
                        hint="Accrued, before payout" negative />
              <StatTile label="Net" value={data.netRevenue} emphasize
                        hint="Revenue minus vendor commission" />
            </div>

            <div className="overflow-x-auto border-t border-ink-200">
              <table className="w-full">
                <thead className="border-b border-ink-200">
                  <tr>
                    <th className="th">Date</th>
                    <th className="th text-right">HOT revenue</th>
                    <th className="th text-right">Platform fee</th>
                    <th className="th text-right">FX margin</th>
                    <th className="th text-right">Vendor commission</th>
                    <th className="th text-right">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {days.map(([date, row]) => (
                    <tr key={date} className="hover:bg-ink-50">
                      <td className="td">{date}</td>
                      <td className="td text-right tabular">${row.hot.toFixed(2)}</td>
                      <td className="td text-right tabular">${row.fee.toFixed(2)}</td>
                      <td className="td text-right tabular">${row.fx.toFixed(2)}</td>
                      <td className="td text-right tabular text-red-700">
                        ${row.commission.toFixed(2)}
                      </td>
                      <td className="td text-right tabular font-medium">
                        ${(row.hot + row.fee + row.fx - row.commission).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                  {days.length === 0 && (
                    <tr><td className="td text-sm text-ink-500" colSpan={6}>
                      No ledger activity in this range.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {confirmingBackfill && <BackfillModal onClose={() => setConfirmingBackfill(false)} />}
    </div>
  );
}

function BackfillModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<BackfillResult | null>(null);

  const backfill = useMutation({
    mutationFn: () => api.post<BackfillResult>('/api/admin/accounting/backfill'),
    onSuccess: (r) => {
      setResult(r);
      queryClient.invalidateQueries({ queryKey: ['accounting-summary'] });
    },
  });

  return (
    <Modal title="Backfill historical orders" onClose={onClose}>
      {!result && (
        <>
          <p className="text-sm text-ink-600">
            Adds the platform fee, vendor commission and FX margin to every past recharge that
            predates them, so the totals above reflect every transaction, not just ones placed
            since each shipped. Safe to run more than once — anything already backfilled is
            skipped.
          </p>
          <p className="mt-2 text-sm text-ink-600">
            There's no historical record of what the USD/ZWG rate, the platform fee, or the ZiG
            acquisition rate were on any given day, so old orders use{' '}
            <span className="font-medium">today's</span> settings as an approximation rather than
            an exact reconstruction.
          </p>
          {backfill.isError && <ErrorNote error={backfill.error} />}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" disabled={backfill.isPending}
                    onClick={() => backfill.mutate()}>
              {backfill.isPending ? 'Backfilling…' : 'Run backfill'}
            </button>
          </div>
        </>
      )}
      {result && (
        <>
          <Banner tone="info">
            Processed {result.ordersProcessed} order{result.ordersProcessed === 1 ? '' : 's'}
            {result.ordersSkipped > 0 && ` (${result.ordersSkipped} already up to date)`} —
            added ${result.feeTotalUsd.toFixed(2)} platform fee revenue,
            ${result.fxMarginTotalUsd.toFixed(2)} FX margin revenue
            {result.commissionTotalUsd > 0
              && `, and $${result.commissionTotalUsd.toFixed(2)} vendor commission expense`}.
          </Banner>
          <div className="mt-4 flex justify-end">
            <button type="button" className="btn-primary" onClick={onClose}>Done</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function StatTile({ label, value, hint, negative, emphasize }: {
  label: string; value: number; hint: string; negative?: boolean; emphasize?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-4 ${
      emphasize ? 'border-ink-300 bg-ink-50' : 'border-ink-200 bg-white'
    }`}>
      <p className="label">{label}</p>
      <p className={`mt-1 text-2xl font-semibold tabular ${negative ? 'text-red-700' : 'text-ink-900'}`}>
        {negative && value > 0 ? '−' : ''}${Math.abs(value).toFixed(2)}
      </p>
      <p className="mt-1 text-xs text-ink-500">{hint}</p>
    </div>
  );
}
